import * as React from "react";
import { BroadStateSelector } from "source/app/presentation/SelectorBoxes/BroadStateSelectors/BroadStateSelectors";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import { GetPercentageColor } from "source/app/services/Color";
import { broadStateSelectorData } from "source/app/presentation/Shared/ActivePanel/BroadStateSelectors/IBroadStateSelector/broadStateSelectorData";
import { FadeInArray } from "../../../Animation/FadeInArray";
import { BroadStateCenterValue } from "./CenterValue/BroadStateCenterValue";

export function ShowBroadStateSelectorHUD({
    averagePercent,
}: {
    averagePercent: number | undefined;
}) {
    // Required to force broad state selectors to mount
    // so their values can be set when the app loads
    const hasAveragePercent = averagePercent !== undefined;
    const displayAveragePercent = averagePercent ?? 0;

    const progressBarPathColor = hasAveragePercent
        ? GetPercentageColor(displayAveragePercent)
        : "#b0803d";
    const size = 280;
    const center = size / 2;
    const stationRadius = 94;
    const stationAngles = broadStateSelectorData.map(
        (_, index) => (360 / broadStateSelectorData.length) * index - 90
    );

    return (
        <div
            className={StyleClass(`broadStateAveragePercent_container`, {
                width: `${size}px`,
                height: `${size}px`,
                margin: "48px auto",
                position: "relative",
                opacity: 0.45,
                ":hover": {
                    opacity: 1,
                },
            })}
            style={{ transition: "opacity 500ms ease" }}
        >
            <svg
                viewBox={`0 0 ${size} ${size}`}
                width="100%"
                height="100%"
                aria-hidden="true"
                style={{ overflow: "visible", position: "absolute", inset: 0 }}
            >
                <defs>
                    <filter id="broad-state-glow" x="-80%" y="-80%" width="260%" height="260%">
                        <feGaussianBlur stdDeviation="3" />
                    </filter>
                    <linearGradient id="broad-state-bronze" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#c49a55" />
                        <stop offset="0.5" stopColor="#684821" />
                        <stop offset="1" stopColor="#b0803d" />
                    </linearGradient>
                </defs>
                {stationAngles.map((angle, index) => {
                    const radians = (angle * Math.PI) / 180;
                    const x = center + Math.cos(radians) * stationRadius;
                    const y = center + Math.sin(radians) * stationRadius;

                    const innerX = center + Math.cos(radians) * 33;
                    const innerY = center + Math.sin(radians) * 33;
                    const outerX = x - Math.cos(radians) * 20;
                    const outerY = y - Math.sin(radians) * 20;

                    return (
                        <g key={angle}>
                            {index % 2 === 0 ? (
                                <>
                                    <line
                                        x1={innerX}
                                        y1={innerY}
                                        x2={outerX}
                                        y2={outerY}
                                        stroke="#110b06"
                                        strokeWidth="8"
                                        opacity="0.96"
                                    />
                                    <line
                                        x1={innerX}
                                        y1={innerY}
                                        x2={outerX}
                                        y2={outerY}
                                        stroke="#805929"
                                        strokeWidth="2.25"
                                        opacity="0.98"
                                    />
                                </>
                            ) : null}
                            <rect
                                x={x - 17}
                                y={y - 17}
                                width="34"
                                height="34"
                                transform={`rotate(45 ${x} ${y})`}
                                fill="rgba(6, 11, 10, 0.88)"
                                stroke="#2a1b0f"
                                strokeWidth="5"
                            />
                            <rect
                                x={x - 17}
                                y={y - 17}
                                width="34"
                                height="34"
                                transform={`rotate(45 ${x} ${y})`}
                                fill="none"
                                stroke="url(#broad-state-bronze)"
                                strokeWidth="1.5"
                            />
                        </g>
                    );
                })}
                <path
                    d={`M ${center} ${center - 31} L ${center + 31} ${center} L ${center} ${center + 31} L ${center - 31} ${center} Z`}
                    fill="#0b0b09"
                    stroke={progressBarPathColor}
                    strokeWidth="5"
                    opacity="0.35"
                    filter="url(#broad-state-glow)"
                />
                <path
                    d={`M ${center} ${center - 31} L ${center + 31} ${center} L ${center} ${center + 31} L ${center - 31} ${center} Z`}
                    fill="#0b0b09"
                    stroke="url(#broad-state-bronze)"
                    strokeWidth="1.5"
                />
                <path
                    d={`M ${center} ${center - 25} L ${center + 25} ${center} L ${center} ${center + 25} L ${center - 25} ${center} Z`}
                    fill="none"
                    stroke="#4b3219"
                    strokeWidth="1"
                />
            </svg>
            <BroadStateCenterValue
                hasAveragePercent={hasAveragePercent}
                displayAveragePercent={displayAveragePercent}
                color={progressBarPathColor}
            />
            <div style={{ position: "absolute", inset: 0, zIndex: 3 }}>
                <FadeInArray
                    label="broadStateSelectorHUD"
                    array={broadStateSelectorData}
                    containerClass={StyleClass(
                        `broadStateSelectorHUD_itemsContainer`,
                        {
                            position: `absolute`,
                            inset: 0,
                        }
                    )}
                    render={(bss, index) => (
                            <BroadStateSelector
                            key={bss.verbSymbol}
                            verbSymbol={bss.verbSymbol}
                            sharedFunction={bss.sharedFunction}
                            rotateDegrees={stationAngles[index!]}
                        />
                    )}
                    itemDelayMs={250}
                />
            </div>
        </div>
    );
}
